import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Form, Modal, Space, Spin } from "antd";
import Title from "antd/lib/typography/Title";
import { linkTSRToCustomer } from "utils/api";
import CustomButton from "components/Common/buttons/CustomButton";
import NGIFormItem from "components/Common/form/NGIFormItem";
import { warnMessage } from "components/Common/responses/message";
import { AUTH_USER, USER_ROLES } from "utils/constants";
import TSRSelect from "components/Common/components/TSRSelect";
import { daysToToday } from "utils/helpers/functions";

const TSRModal = ({ onClose, isOpen, TSRList, tsrType = null, customer, onUpdate }) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTSR, setSelectedTSR] = useState(null);
  const { installationDate } = customer;
  const ageAsCustomer = daysToToday(installationDate);

  const isGrowthTsr = useMemo(
    () => tsrType === "growthTechnician" || (tsrType == null && ageAsCustomer <= 30),
    [tsrType, ageAsCustomer]
  );
  const isCsaTsr = tsrType === "csaTechnician";
  const tsrTeam = isCsaTsr ? "CSA" : isGrowthTsr ? "Growth" : "Maintenance";
  const tsrField = isCsaTsr ? "csaTechnician" : isGrowthTsr ? "growthTechnician" : "technician";

  const handleCancel = useCallback(() => onClose(), [onClose]);

  const handleSave = useCallback(() => {
    setIsLoading(true);
    const preparedDate = {
      [tsrField]: selectedTSR.email,
      [`${tsrField}Name`]: selectedTSR.name,
    };
    linkTSRToCustomer(customer.id, preparedDate).then(() => {
      onUpdate({ ...customer, ...preparedDate });
      onClose();
      warnMessage("Customer is successfully assigned to the new TSR.");
      setIsLoading(false);
    });
  }, [onUpdate, selectedTSR, customer, tsrField, onClose]);

  const handleChange = useCallback(technician => setSelectedTSR(technician), []);

  const tsrList = useMemo(() => TSRList.filter(item => item.team === tsrTeam), [TSRList, tsrTeam]);

  useEffect(() => {
    if (tsrList.length !== 1) return;
    const [singleTsr] = tsrList;
    setSelectedTSR(singleTsr);
    form.setFieldsValue({ tsr: singleTsr.id });
  }, [tsrList, form]);

  return (
    <Modal open={isOpen} title="Link customer to TSR" closable={false} onCancel={handleCancel} footer={[]}>
      <>
        <Spin spinning={isLoading}>
          <Form name="customerToTsr" form={form} onFinish={handleSave}>
            <Col span={24}>
              <Title level={5}>{tsrTeam} TSR</Title>
              <NGIFormItem name="tsr" rules={[{ required: true, message: "Please select TSR" }]}>
                <TSRSelect
                  showSearch
                  filterBy={{
                    field: "team",
                    value: [tsrTeam],
                  }}
                  initialList={tsrList}
                  handleChange={handleChange}
                  roles={[USER_ROLES.MMT, USER_ROLES.TECHNICIANS, USER_ROLES.GCR, USER_ROLES.MTSR]}
                  depot={AUTH_USER()?.depotName}
                />
              </NGIFormItem>
            </Col>
            <Space>
              <CustomButton onClick={handleCancel} type="primary" color="outlined" size="small" text="Cancel" />
              <CustomButton disabled={!selectedTSR} htmlType="submit" type="primary" size="small" text="Save" />
            </Space>
          </Form>
        </Spin>
      </>
    </Modal>
  );
};

export default TSRModal;
